import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getNovedades,
  crearNovedad,
  eliminarNovedadApi,
  getEmpleadosHorarios,
  getSedesHorarios
} from '../services/horariosApi';
import { resolverAlcanceABAC } from '../utils/abacHorarios';

const TIPOS_NOVEDAD = [
  { id: 'HORA_EXTRA', label: '⏱️ Hora Extra (Sujeta a Aprobación)', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  { id: 'INCAPACIDAD', label: '🏥 Incapacidad Médica', color: 'bg-rose-100 text-rose-800 border-rose-200' },
  { id: 'VACACIONES', label: '🏖️ Vacaciones', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { id: 'LICENCIA', label: '📄 Licencia / Permiso Remunerado', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  { id: 'FALTA', label: '⚠️ Ausencia / Falta Injustificada', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  { id: 'CALAMIDAD', label: '🏠 Calamidad Doméstica', color: 'bg-teal-100 text-teal-800 border-teal-200' }
];

export default function NovedadesHorarios({ usuario, onVolver }) {
  const [novedades, setNovedades] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);

  const hoyStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  const ayerStr = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0];
  }, []);

  const [form, setForm] = useState({ id_empleado: '', tipo_novedad: 'HORA_EXTRA', fecha_inicio: hoyStr, fecha_fin: hoyStr, horas_extra: 2, motivo: '' });

  const mostrarToast = useCallback((mensaje, tipo = 'success') => {
    setToast({ mensaje, tipo });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const cargarTodo = useCallback(async () => {
    setCargando(true);
    try {
      const [resNov, resEmp, resSedes] = await Promise.all([
        getNovedades().catch(() => ({ success: false, data: [] })),
        getEmpleadosHorarios().catch(() => ({ success: false, data: [] })),
        getSedesHorarios().catch(() => ({ success: false, data: [] }))
      ]);
      if (resNov?.success) setNovedades(resNov.data || []);
      if (resEmp?.success) setEmpleados(resEmp.data || []);
      if (resSedes?.success) setSedes(resSedes.data || []);
    } catch (error) { mostrarToast('Error cargando novedades: ' + error.message, 'error'); } 
    finally { setCargando(false); }
  }, [mostrarToast]);

  useEffect(() => { cargarTodo(); }, [cargarTodo]);

  const alcanceABAC = useMemo(() => resolverAlcanceABAC(usuario, sedes), [usuario, sedes]);
  const prefijosPermitidos = useMemo(() => new Set((alcanceABAC.sedesPermitidas || []).map(s => String(s.prefijo || '').toUpperCase())), [alcanceABAC]);

  const empleadosPermitidos = useMemo(() => {
    return empleados
      .filter(e => {
        if (String(e.estado || 'ACTIVO').toUpperCase() !== 'ACTIVO' || !e.codigo_interno) return false;
        if (alcanceABAC.esGlobal) return true;
        const pref = String(e.codigo_interno).split('-')[0].toUpperCase();
        return prefijosPermitidos.has(pref);
      })
      .sort((a, b) => {
        // Ordenamos primero por Sede y luego alfabéticamente para que el desplegable sea limpio
        const sedeA = a.sede_nombre || ''; const sedeB = b.sede_nombre || '';
        if (sedeA !== sedeB) return sedeA.localeCompare(sedeB);
        return (a.nombre_completo || '').localeCompare(b.nombre_completo || '');
      });
  }, [empleados, alcanceABAC, prefijosPermitidos]);

  const empleadoSeleccionado = useMemo(() => {
    return empleadosPermitidos.find(e => String(e.id) === String(form.id_empleado)) || null;
  }, [empleadosPermitidos, form.id_empleado]);

  const novedadesPermitidas = useMemo(() => {
    if (alcanceABAC.esGlobal) return novedades;
    return novedades.filter(n => {
      const pref = String(n.codigo_interno || '').split('-')[0].toUpperCase();
      return prefijosPermitidos.has(pref);
    });
  }, [novedades, alcanceABAC, prefijosPermitidos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_empleado) return mostrarToast('Selecciona un colaborador', 'error');

    const esExtra = form.tipo_novedad === 'HORA_EXTRA';
    setGuardando(true);
    try {
      const payload = {
        id_empleado: Number(form.id_empleado),
        tipo_novedad: form.tipo_novedad || 'HORA_EXTRA',
        fecha_inicio: form.fecha_inicio,
        fecha_fin: esExtra ? form.fecha_inicio : (form.fecha_fin || form.fecha_inicio),
        horas_extra: esExtra ? Number(form.horas_extra || 1) : 0,
        motivo: form.motivo.trim(),
        solicitado_por: usuario?.nombres || usuario?.nombre || 'Administrador de Punto',
        es_gerencia: alcanceABAC.esGlobal
      };

      const res = await crearNovedad(payload);
      mostrarToast(res.message || 'Novedad registrada correctamente', 'success');
      setModalAbierto(false);
      cargarTodo();
    } catch (error) { mostrarToast(error.message, 'error'); } 
    finally { setGuardando(false); }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Deseas anular esta novedad y restaurar el turno en la sábana?')) return;
    try {
      await eliminarNovedadApi(id);
      mostrarToast('Novedad anulada y sábana restaurada', 'success');
      cargarTodo();
    } catch (error) { mostrarToast(error.message, 'error'); }
  };

  const renderBadgeTipo = (nov) => {
    const esExtra = String(nov.tipo_novedad || '').toUpperCase().includes('EXTRA') || Number(nov.horas_extra || 0) > 0;
    if (esExtra) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-purple-100 text-purple-800 border border-purple-200">
          ⏱️ HORA EXTRA (+{Number(nov.horas_extra).toFixed(1)}h)
        </span>
      );
    }
    const found = TIPOS_NOVEDAD.find(t => t.id === String(nov.tipo_novedad).toUpperCase());
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black border ${found ? found.color : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
        {found ? found.label : (nov.tipo_novedad || 'NOVEDAD')}
      </span>
    );
  };

  const renderBadgeEstado = (nov) => {
    const esExtra = String(nov.tipo_novedad || '').toUpperCase().includes('EXTRA') || Number(nov.horas_extra || 0) > 0;
    const est = String(nov.estado_aprobacion || 'APROBADA').toUpperCase();

    if (esExtra) {
      if (est === 'APROBADA') return <span className="px-3 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-200">✓ APROBADA EN SÁBANA</span>;
      if (est === 'RECHAZADA') return <span className="px-3 py-1 rounded-full text-[10px] font-black bg-rose-100 text-rose-800 border border-rose-200">✕ RECHAZADA</span>;
      return <span className="px-3 py-1 rounded-full text-[10px] font-black bg-amber-100 text-amber-800 border border-amber-200">⏳ PENDIENTE GERENCIA</span>;
    }
    return <span className="px-3 py-1 rounded-full text-[10px] font-black bg-slate-100 text-slate-700 border border-slate-200">APLICADA EN SÁBANA</span>;
  };

  return (
    <div className="animate-fade-in relative pb-10">
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-5 py-3.5 rounded-2xl shadow-xl font-black text-xs text-white ${toast.tipo === 'error' ? 'bg-rose-600' : 'bg-slate-900'}`}>
          {toast.mensaje}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-200 pb-5 mb-6 gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center text-2xl">🏥</div>
          <div>
            <h2 className="text-2xl font-black text-slate-800">Novedades y Horas Extras</h2>
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
              Recálculo automático en sábana • Ventana retroactiva (Desde: {ayerStr})
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setModalAbierto(true)} className="px-5 py-2.5 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs shadow-md transition cursor-pointer">
            + Registrar Novedad
          </button>
          <button onClick={onVolver} className="px-5 py-2.5 rounded-2xl bg-white hover:bg-gray-50 text-slate-700 font-bold text-xs border border-gray-200 shadow-sm cursor-pointer">
            Volver
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-gray-50 text-gray-500 uppercase font-black border-b border-gray-100">
            <tr>
              <th className="p-4">Empleado / Sede</th>
              <th className="p-4">Tipo / Motivo</th>
              <th className="p-4">Fechas</th>
              <th className="p-4 text-center">Estado</th>
              <th className="p-4 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {cargando ? (
              <tr><td colSpan="5" className="p-12 text-center font-bold text-gray-400">Cargando novedades...</td></tr>
            ) : novedadesPermitidas.length === 0 ? (
              <tr><td colSpan="5" className="p-12 text-center font-bold text-gray-400">No hay novedades registradas.</td></tr>
            ) : (
              novedadesPermitidas.map(nov => (
                <tr key={nov.id} className="hover:bg-slate-50/80 transition">
                  <td className="p-4">
                    <span className="font-black text-slate-900 block">{nov.nombre_completo}</span>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-mono font-black text-[9px] border border-blue-100">
                      {nov.codigo_interno} • {nov.sede_nombre}
                    </span>
                  </td>
                  <td className="p-4">
                    <div>{renderBadgeTipo(nov)}</div>
                    {nov.motivo && <p className="text-[11px] text-gray-500 italic mt-1 max-w-xs truncate" title={nov.motivo}>"{nov.motivo}"</p>}
                  </td>
                  <td className="p-4 font-mono font-bold text-slate-700">
                    {nov.fecha_inicio === nov.fecha_fin ? nov.fecha_inicio : `${nov.fecha_inicio} al ${nov.fecha_fin}`}
                  </td>
                  <td className="p-4 text-center">{renderBadgeEstado(nov)}</td>
                  <td className="p-4 text-center">
                    <button onClick={() => handleEliminar(nov.id)} className="px-4 py-1.5 rounded-xl bg-slate-100 hover:bg-rose-50 hover:text-rose-700 text-slate-600 font-bold text-[11px] transition cursor-pointer">
                      🗑️ Anular
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL CORREGIDO: max-h-[90vh] overflow-y-auto asegura que no se desborde nunca en pantallas pequeñas */}
      {modalAbierto && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-gray-100 animate-fade-in max-h-[90vh] overflow-y-auto custom-scrollbar">
            
            <div className="flex justify-between items-center border-b pb-4 mb-5 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-black text-slate-900">Registrar Novedad</h3>
              <button onClick={() => setModalAbierto(false)} className="w-8 h-8 rounded-full bg-gray-100 font-bold text-gray-600 cursor-pointer hover:bg-gray-200">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Colaborador</label>
                <select
                  required
                  value={form.id_empleado}
                  onChange={(e) => setForm({ ...form, id_empleado: e.target.value })}
                  className="w-full p-3 rounded-xl border border-gray-200 text-xs font-bold text-slate-800"
                >
                  <option value="">Selecciona colaborador...</option>
                  {empleadosPermitidos.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      [{emp.sede_nombre}] {emp.codigo_interno} — {emp.nombre_completo}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Tipo de Novedad</label>
                <select
                  value={form.tipo_novedad}
                  onChange={(e) => setForm({ ...form, tipo_novedad: e.target.value })}
                  className="w-full p-3 rounded-xl border border-gray-200 text-xs font-bold text-slate-800"
                >
                  {TIPOS_NOVEDAD.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* PANEL DE AUDITORÍA WFM (Aviso dinámico de topes) */}
              {form.tipo_novedad === 'HORA_EXTRA' && empleadoSeleccionado && (
                <div className="bg-purple-50 p-3 rounded-xl border border-purple-100 flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">ℹ️</span>
                  <div>
                    <p className="text-[10px] font-black text-purple-800 uppercase">Política del Cargo: {empleadoSeleccionado.cargo_nombre || empleadoSeleccionado.cargo_original}</p>
                    <p className="text-[11px] text-purple-700 font-medium">Este colaborador tiene un tope legal autorizado de máximo <b>{empleadoSeleccionado.max_extras_cargo} horas extras semanales</b>. Las solicitudes superiores quedarán bajo estricta auditoría de Gerencia.</p>
                  </div>
                </div>
              )}

              {form.tipo_novedad === 'HORA_EXTRA' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Fecha del Turno</label>
                    <input
                      type="date"
                      required
                      min={alcanceABAC.esGlobal ? undefined : ayerStr}
                      value={form.fecha_inicio}
                      onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value, fecha_fin: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-purple-700 uppercase mb-1">Cantidad Horas Extra</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="12"
                      required
                      value={form.horas_extra}
                      onChange={(e) => setForm({ ...form, horas_extra: e.target.value })}
                      className="w-full p-3 rounded-xl border border-purple-300 bg-purple-50/40 text-xs font-black text-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Fecha Inicio</label>
                    <input
                      type="date"
                      required
                      min={alcanceABAC.esGlobal ? undefined : ayerStr}
                      value={form.fecha_inicio}
                      onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Fecha Fin</label>
                    <input
                      type="date"
                      required
                      min={form.fecha_inicio}
                      value={form.fecha_fin}
                      onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 text-xs font-bold"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Motivo / Justificación</label>
                <textarea
                  rows="2"
                  required
                  value={form.motivo}
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                  placeholder={form.tipo_novedad === 'HORA_EXTRA' ? "Ej: Apoyo en cierre por ausencia de compañero..." : "Describe brevemente la razón..."}
                  className="w-full p-3 rounded-xl border border-gray-200 text-xs font-medium"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 mt-2">
                <button type="button" onClick={() => setModalAbierto(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-bold text-xs cursor-pointer hover:bg-gray-200 transition">
                  Cancelar
                </button>
                <button type="submit" disabled={guardando} className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs shadow-md cursor-pointer transition">
                  {guardando ? 'Procesando...' : form.tipo_novedad === 'HORA_EXTRA' ? 'Enviar Solicitud a Gerencia' : 'Guardar y Recalcular'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}